import React, { FC } from 'react';
import { Trans } from 'react-i18next';

import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import { ExpandIcon } from 'components';
import { ScopeCode } from 'config/scopes';

import { ScopeGrantMap, ScopeRow, ScopeSection } from './usePermissionGroupScopes';

const GRANT_COLUMN_WIDTH = 160;

export interface PermissionGroupScopesTabProps {
  sections: ScopeSection[];
  grants: ScopeGrantMap;
  onChange: (code: ScopeCode, field: 'view' | 'edit', value: boolean) => void;
  canEdit: boolean;
}

const ScopeRowView: FC<{
  row: ScopeRow;
  grants: ScopeGrantMap;
  onChange: (code: ScopeCode, field: 'view' | 'edit', value: boolean) => void;
  canEdit: boolean;
  indent?: boolean;
  parentViewOn?: boolean;
}> = ({ row, grants, onChange, canEdit, indent, parentViewOn }) => {
  const grant = grants[row.entry.code] ?? { view: false, edit: false };
  const rowDisabled = !canEdit || (indent ? !parentViewOn : false);

  return (
    <>
      <TableRow>
        <TableCell sx={indent ? { pl: 5 } : undefined}>
          <Typography variant="body2">{row.label}</Typography>
        </TableCell>
        <TableCell align="center" sx={{ width: GRANT_COLUMN_WIDTH }}>
          <Checkbox
            checked={grant.view}
            disabled={rowDisabled || grant.edit}
            onChange={(_e, checked) => onChange(row.entry.code, 'view', checked)}
          />
        </TableCell>
        <TableCell align="center" sx={{ width: GRANT_COLUMN_WIDTH }}>
          <Checkbox
            checked={grant.edit}
            disabled={rowDisabled}
            onChange={(_e, checked) => onChange(row.entry.code, 'edit', checked)}
          />
        </TableCell>
      </TableRow>
      {row.children.map((child) => (
        <ScopeRowView
          key={child.code}
          row={{ entry: child, label: child.label, children: [] }}
          grants={grants}
          onChange={onChange}
          canEdit={canEdit}
          indent
          parentViewOn={grant.view}
        />
      ))}
    </>
  );
};

export const PermissionGroupScopesTab: FC<PermissionGroupScopesTabProps> = ({
  sections,
  grants,
  onChange,
  canEdit,
}) => {
  return (
    <Box>
      {sections.map((section) => (
        <Accordion key={section.key} defaultExpanded>
          <AccordionSummary expandIcon={<ExpandIcon open />}>
            <Typography variant="subtitle1">{section.label}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Table size="small" sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Trans i18nKey="KOZOS_JOGOSULTSAGI_CSOPORTOK.COLUMN_SCREEN" />
                  </TableCell>
                  <TableCell align="center" sx={{ width: GRANT_COLUMN_WIDTH }}>
                    <Trans i18nKey="KOZOS_JOGOSULTSAGI_CSOPORTOK.COLUMN_VIEW" />
                  </TableCell>
                  <TableCell align="center" sx={{ width: GRANT_COLUMN_WIDTH }}>
                    <Trans i18nKey="KOZOS_JOGOSULTSAGI_CSOPORTOK.COLUMN_EDIT" />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {section.groups.map((scopeGroup) => (
                  <React.Fragment key={scopeGroup.key}>
                    {scopeGroup.label && (
                      <TableRow>
                        <TableCell colSpan={3} sx={{ backgroundColor: 'action.hover' }}>
                          <Typography variant="subtitle2">{scopeGroup.label}</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {scopeGroup.rows.map((row) => (
                      <ScopeRowView
                        key={row.entry.code}
                        row={row}
                        grants={grants}
                        onChange={onChange}
                        canEdit={canEdit}
                      />
                    ))}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default PermissionGroupScopesTab;
